import { oc } from "@orpc/contract";
import { z } from "zod";

// ─── Shared schemas ─────────────────────────────────────────────────────────

export const InventorySnapshotRowSchema = z.object({
  warehouseId: z.string().uuid(),
  warehouseName: z.string(),
  warehouseType: z.string().nullable(),
  productId: z.string().uuid(),
  productSku: z.string().nullable(),
  productName: z.string(),
  snapshotDate: z.string(),
  onHandQuantity: z.number(),
  reservedQuantity: z.number(),
  availableQuantity: z.number(),
  hasLots: z.boolean(),
  lastSyncedAt: z.string(),
  // Inventory-policy lookup (per product × warehouse). Nullable when the
  // SKU/warehouse pair has no policy row in `im_inventory_policies` yet.
  popularityCode: z.string().nullable(),
  stockStatus: z.string().nullable(),
});

export type InventorySnapshotRow = z.infer<typeof InventorySnapshotRowSchema>;

// ─── List ───────────────────────────────────────────────────────────────────

export const ListInventorySnapshotsInputSchema = z.object({
  divisionId: z.string().uuid().optional(),
  warehouseId: z.string().uuid().optional(),
  search: z.string().optional(),
  snapshotDate: z.string().optional(),
  hideZeroQuantity: z.boolean().optional().default(false),
  warehouseNames: z.array(z.string()).optional(),
  hasLots: z.boolean().optional(),
  page: z.number().int().min(1).optional().default(1),
  pageSize: z.number().int().min(1).max(500).optional().default(50),
});

export const ListInventorySnapshotsResponseSchema = z.object({
  rows: z.array(InventorySnapshotRowSchema),
  totalRows: z.number(),
  effectiveSnapshotDate: z.string().nullable(),
});

export type ListInventorySnapshotsInput = z.infer<
  typeof ListInventorySnapshotsInputSchema
>;
export type ListInventorySnapshotsResponse = z.infer<
  typeof ListInventorySnapshotsResponseSchema
>;

// ─── Product detail ─────────────────────────────────────────────────────────

export const GetProductInventoryDetailInputSchema = z.object({
  productId: z.string().uuid(),
  snapshotDate: z.string().optional(),
});

export const ProductWarehouseSnapshotSchema = z.object({
  warehouseId: z.string().uuid(),
  warehouseName: z.string(),
  warehouseType: z.string().nullable(),
  divisionId: z.string().uuid().nullable(),
  divisionName: z.string().nullable(),
  onHandQuantity: z.number(),
  reservedQuantity: z.number(),
  availableQuantity: z.number(),
  lastSyncedAt: z.string(),
  popularityCode: z.string().nullable(),
  stockStatus: z.string().nullable(),
  // Buyer's per-item source decision (INITINFC giparent1) for this SKU at this
  // warehouse: 'transfer' (from sourceWarehouseName), 'buy', or null (unset).
  sourceType: z.enum(["transfer", "buy"]).nullable(),
  sourceWarehouseId: z.string().uuid().nullable(),
  sourceWarehouseName: z.string().nullable(),
  // Forecasting-engine outputs on this SKU×warehouse policy (null until the
  // engine has computed them). Demand-driven buffer + reorder level, the
  // service level they target, and when they were last computed.
  safetyStock: z.number().nullable(),
  reorderPoint: z.number().nullable(),
  orderUpToLevel: z.number().nullable(),
  targetAvailability: z.number().nullable(),
  forecastCalculatedAt: z.string().nullable(),
});

export const ProductInventoryLotSchema = z.object({
  warehouseId: z.string().uuid(),
  warehouseName: z.string(),
  unitNumber: z.string(),
  quantity: z.number(),
});

export const GetProductInventoryDetailResponseSchema = z.object({
  productId: z.string().uuid(),
  productSku: z.string().nullable(),
  productName: z.string(),
  originId: z.string().nullable(),
  originSystem: z.string().nullable(),
  isActive: z.boolean(),
  effectiveSnapshotDate: z.string().nullable(),
  totalOnHand: z.number(),
  totalReserved: z.number(),
  totalAvailable: z.number(),
  warehouses: z.array(ProductWarehouseSnapshotSchema),
  lots: z.array(ProductInventoryLotSchema),
});

export type GetProductInventoryDetailInput = z.infer<
  typeof GetProductInventoryDetailInputSchema
>;
export type GetProductInventoryDetailResponse = z.infer<
  typeof GetProductInventoryDetailResponseSchema
>;
export type ProductWarehouseSnapshot = z.infer<
  typeof ProductWarehouseSnapshotSchema
>;
export type ProductInventoryLot = z.infer<typeof ProductInventoryLotSchema>;

// ─── Contract ───────────────────────────────────────────────────────────────

export const inventorySnapshotsContracts = {
  list: oc
    .input(ListInventorySnapshotsInputSchema)
    .output(ListInventorySnapshotsResponseSchema)
    .route({
      method: "GET",
      summary:
        "List inventory snapshot rows (warehouse × product × date) for the current org. Defaults to the latest snapshot date.",
    }),
  getByProduct: oc
    .input(GetProductInventoryDetailInputSchema)
    .output(GetProductInventoryDetailResponseSchema)
    .route({
      method: "GET",
      summary:
        "Get product metadata and per-warehouse inventory breakdown for a single product.",
    }),
} as const;
